import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, Remove, Save } from "@mui/icons-material";
import moment from "moment-timezone";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import CloseButton from "../../../../components/commonComponents/Buttons/CloseButton";
import Confirmation from "../../../../components/commonComponents/Confirmation";
import DateInputField from "../../../../components/commonComponents/TextInputs/DateInputs";
import TimePickerField from "../../../../components/commonComponents/TextInputs/TimePickerField";
import {
  HtmlTooltipDisabled,
  StyledCheckbox,
} from "../../../../components/commonComponents/StyledComponents/styled";
import { toast } from "react-toastify";
import { saveQuietHours, updateQuietHours, normalizeCloseTime, isInvertedRange } from "../../../../api_calls/infrastructure";
import SingleSelect from "../../../../components/commonComponents/Selectors/SingleSelect";
import { WEEKDAY_KEYS, WEEKDAY_DISPLAY_ORDER, WEEKDAY_INT_TO_LOWER, getQuietHoursStatus } from "../../../../api_calls/utils";

const WEEKDAY_SHORT = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

const DEFAULT_QUIET_RANGE = () => ({ start: "08:00", end: "20:00" });

const detectRangeOverlap = (ranges, idx) => {
  const r = ranges[idx];
  if (!r || !r.start || !r.end) return false;
  const rEnd = normalizeCloseTime(r.end);
  return ranges.some((other, i) => {
    if (i === idx) return false;
    if (!other.start || !other.end) return false;
    const otherEnd = normalizeCloseTime(other.end);
    return !(rEnd <= other.start || r.start >= otherEnd);
  });
};

const QuietTimeRangeBlock = ({ ranges, onChange, showEmptyErrors, disabled }) => {
  const handleChangeField = (idx, field, val) => {
    onChange(ranges.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const handleAdd = () => {
    if (disabled || ranges.length >= 5) return;
    onChange([...ranges, { start: "", end: "" }]);
  };

  const handleRemove = (idx) => {
    if (disabled) return;
    onChange(ranges.filter((_, i) => i !== idx));
  };

  return (
    // Small vertical gap between stacked time ranges so multiple ranges read as
    // separate rows rather than butting together.
    <Stack spacing={0.75}>
      {ranges.map((r, idx) => {
        const hasOverlap = detectRangeOverlap(ranges, idx);
        const inverted = isInvertedRange(r.start, r.end);
        const isFirst = idx === 0;
        return (
          <Box key={idx} sx={{ pt: isFirst && ranges.length > 1 ? 0.5 : 0 }}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <Box sx={{ width: 130 }}>
                <HtmlTooltipDisabled title={disabled ? "Not available while the schedule is active" : ""}>
                  <span>
                    <TimePickerField
                      elemId={"quiet_start_" + idx}
                      label={isFirst ? "Start" : ""}
                      defaultVal={r.start}
                      disabled={disabled}
                      onChangeCall={(val) => handleChangeField(idx, "start", val)}
                    />
                  </span>
                </HtmlTooltipDisabled>
              </Box>
              <Typography variant="body2" sx={{ pb: 1 }}>
                to
              </Typography>
              <Box sx={{ width: 130 }}>
                <HtmlTooltipDisabled title={disabled ? "Not available while the schedule is active" : ""}>
                  <span>
                    <TimePickerField
                      elemId={"quiet_end_" + idx}
                      label={isFirst ? "End" : ""}
                      defaultVal={r.end}
                      disabled={disabled}
                      onChangeCall={(val) => handleChangeField(idx, "end", val)}
                    />
                  </span>
                </HtmlTooltipDisabled>
              </Box>
              {/* + on the labeled row: offset by the label band so it sits on the
                  value line like − on unlabeled rows. */}
              {isFirst && ranges.length < 5 && (
                <Tooltip title={disabled ? "Not available while the schedule is active" : "Add time range"}>
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignSelf: "flex-start", pt: "14px" }}
                  >
                    <IconButton size="small" onClick={handleAdd} disabled={disabled}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
              {!isFirst && (
                <Tooltip title={disabled ? "Not available while the schedule is active" : "Remove time range"}>
                  <Box component="span" sx={{ display: "inline-flex", pb: 0.5 }}>
                    <IconButton size="small" onClick={() => handleRemove(idx)} disabled={disabled}>
                      <Remove fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
            </Stack>
            {
              inverted && (
                <FormHelperText error sx={{ ml: 0, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.3 }}>
                  End time must be after start time
                </FormHelperText>
              )
            }
            {
              !inverted && hasOverlap && (
                <FormHelperText error sx={{ ml: 0, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.3 }}>
                  Overlaps with another time range
                </FormHelperText>
              )
            }
            {/* Empty-field error surfaces only on save (showEmptyErrors), spans the
               whole row like the other errors, and clears once both times are set. */}
            {
              !inverted && !hasOverlap && showEmptyErrors && (!r.start || !r.end) && (
                <FormHelperText error sx={{ ml: 0, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.3 }}>
                  Start and end time required
                </FormHelperText>
              )
            }
          </Box>
        );
      })}
    </Stack >
  );
};

const DEFAULT_REPEAT_ON = () => WEEKDAY_KEYS.reduce((acc, d) => ({ ...acc, [d]: false }), {});

const EMPTY_ENTRY = () => ({
  id: null,
  type: "once",
  validity_start: null,
  validity_end: null,
  no_end: false,
  valid_on: null,
  repeat_on: DEFAULT_REPEAT_ON(),
  time_ranges: [DEFAULT_QUIET_RANGE()],
});

const normalizeRepeatOn = (repeatOn) => {
  if (!repeatOn) return DEFAULT_REPEAT_ON();
  if (Array.isArray(repeatOn)) {
    return repeatOn.reduce((acc, d) => ({ ...acc, [d]: true }), DEFAULT_REPEAT_ON());
  }
  return { ...DEFAULT_REPEAT_ON(), ...repeatOn };
};

const repeatOnToArray = (repeatOn) => {
  if (!repeatOn) return [];
  if (Array.isArray(repeatOn)) return [...repeatOn];
  return WEEKDAY_KEYS.filter((d) => !!repeatOn[d]);
};

// Dates occupied by an existing entry — used to highlight the date picker.
// Duplicate blocking is once-vs-once only; weekly may overlap (ranges are allowed).
const entryCoversDate = (existing, date) => {
  if (!date) return false;
  if (existing.type === "once") return (existing.valid_on || null) === date;
  if (existing.validity_start && date < existing.validity_start) return false;
  if (!existing.no_end && existing.validity_end && date > existing.validity_end) return false;
  return repeatOnToArray(existing.repeat_on).includes(
    WEEKDAY_INT_TO_LOWER[moment(date, "YYYY-MM-DD").day()]
  );
};

const DUPLICATE_ONCE_MSG = "A quiet hours entry already exists for this date";

const EXISTING_QUIET_HIGHLIGHT = [
  {
    key: "existing",
    color: "rgba(240, 75, 85, 0.25)",
    borderColor: "rgba(240, 75, 85, 0.5)",
    label: "Existing quiet hours",
  },
];

const formatLocalIsoDate = (dateObj) =>
  `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;

// Once + once on the same date is blocked (live, same as special hours).
// Weekly may overlap existing entries; multiple time ranges on a day are allowed.
const findDuplicateOnceError = (form, candidates) => {
  if (form.type !== "once" || !form.valid_on) return null;
  const taken = candidates.some(
    (e) => e.type === "once" && (e.valid_on || null) === form.valid_on
  );
  return taken ? DUPLICATE_ONCE_MSG : null;
};

const AddEditQuietHoursDialog = ({ open, mode, entry, storeId, existingEntries, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = moment(today, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
  const [form, setForm] = React.useState(EMPTY_ENTRY());
  const [errors, setErrors] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
  const originalRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const sortByKey = (arr, key) => [...(arr || [])].sort((a, b) => (a[key] > b[key] ? 1 : -1));
    const initial = entry
      ? {
        ...EMPTY_ENTRY(),
        ...entry,
        repeat_on: normalizeRepeatOn(entry.repeat_on),
        time_ranges: sortByKey(entry.time_ranges, "start"),
      }
      : EMPTY_ENTRY();
    setForm(initial);
    setErrors({});
    originalRef.current = JSON.stringify(initial);
  }, [open, entry]);

  const patch = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const isDirty = () => JSON.stringify(form) !== originalRef.current;

  const selectedWeekdays = WEEKDAY_KEYS.filter((d) => !!form.repeat_on?.[d]);
  const yesterday = moment(today, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD");
  const scheduleStatus = mode === "edit" && entry?.id ? getQuietHoursStatus(entry, today) : "new";
  // Active: type, start/valid-on, and times are view-only. End / No End stay editable.
  const isActiveEdit = scheduleStatus === "active";
  // Active end may be yesterday; create/copy/scheduled cannot be before start (or tomorrow if empty).
  const endMinDate = isActiveEdit
    ? (form.validity_start && form.validity_start > yesterday ? form.validity_start : yesterday)
    : (form.validity_start && form.validity_start >= tomorrow ? form.validity_start : tomorrow);

  const otherEntries = React.useMemo(
    () => (existingEntries || []).filter((e) => !(mode === "edit" && entry && e.id === entry.id)),
    [existingEntries, mode, entry]
  );

  const highlightQuietDate = (dateObj) => {
    const d = formatLocalIsoDate(dateObj);
    return otherEntries.some((e) => entryCoversDate(e, d)) ? "existing" : null;
  };
  const quietLegend = otherEntries.length > 0 ? EXISTING_QUIET_HIGHLIGHT : undefined;

  // Live duplicate-date check for type=once: error on set, not only on save (same as special hours).
  React.useEffect(() => {
    if (!open) return;
    const dupMsg = findDuplicateOnceError(form, otherEntries);
    setErrors((prev) => {
      if (dupMsg) {
        if (prev.valid_on === dupMsg) return prev;
        return { ...prev, valid_on: dupMsg };
      }
      if (!prev.valid_on) return prev;
      if (form.valid_on || prev.valid_on === DUPLICATE_ONCE_MSG) {
        const { valid_on, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, [open, form.type, form.valid_on, otherEntries]);

  // Weekdays that actually fall within the chosen validity range.
  // Open-ended schedules or missing endpoints → assume all weekdays available.
  const availableWeekdaySet = React.useMemo(() => {
    if (form.no_end) return new Set(WEEKDAY_KEYS);
    if (!form.validity_start || !form.validity_end) return new Set(WEEKDAY_KEYS);
    const start = moment(form.validity_start, "YYYY-MM-DD");
    const end = moment(form.validity_end, "YYYY-MM-DD");
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return new Set(WEEKDAY_KEYS);
    const diffDays = end.diff(start, "days");
    if (diffDays >= 6) return new Set(WEEKDAY_KEYS);
    const set = new Set();
    for (let i = 0; i <= diffDays; i++) {
      set.add(WEEKDAY_INT_TO_LOWER[start.clone().add(i, "days").day()]);
    }
    return set;
  }, [form.no_end, form.validity_start, form.validity_end]);

  // Auto-uncheck weekdays that fall outside the validity range when it shrinks.
  React.useEffect(() => {
    if (form.type !== "weekly") return;
    if (isActiveEdit) return;
    let changed = false;
    const cleaned = { ...form.repeat_on };
    for (const d of WEEKDAY_KEYS) {
      if (cleaned[d] && !availableWeekdaySet.has(d)) {
        cleaned[d] = false;
        changed = true;
      }
    }
    if (changed) setForm((prev) => ({ ...prev, repeat_on: cleaned }));
  }, [availableWeekdaySet, form.type, isActiveEdit]);

  const validate = () => {
    const errs = {};

    if (form.type === "once") {
      if (!form.valid_on) {
        errs.valid_on = "Valid on is required";
      } else if (!isActiveEdit && form.valid_on < tomorrow) {
        errs.valid_on = "Date cannot be today or in the past";
      }
    }

    if (form.type === "weekly") {
      if (!form.validity_start) {
        errs.validity_start = "Start date is required";
      } else if (!isActiveEdit && form.validity_start < tomorrow) {
        errs.validity_start = "Date cannot be today or in the past";
      }
      if (!form.no_end && !form.validity_end) {
        errs.validity_end = "End date is required";
      } else if (!form.no_end && form.validity_end) {
        if (form.validity_start && form.validity_end < form.validity_start) {
          errs.validity_end = "End date cannot be before start date";
        } else if (isActiveEdit && form.validity_end < yesterday) {
          errs.validity_end = "End date cannot be earlier than yesterday";
        }
      }
      if (selectedWeekdays.length === 0) errs.repeat_on = "At least one weekday must be selected";
    }

    // Flag empties first (across all rows) so the per-row empty errors always show
    // even when another row is inverted/overlapping.
    if (form.time_ranges.some((r) => !r.start || !r.end)) {
      errs.time_ranges = "All ranges require start and end times";
    } else {
      for (const [i, r] of form.time_ranges.entries()) {
        if (isInvertedRange(r.start, r.end)) {
          errs.time_ranges = "End time must be after start time";
          break;
        }
        if (detectRangeOverlap(form.time_ranges, i)) {
          errs.time_ranges = "Overlaps with another time range";
          break;
        }
      }
    }

    const dupMsg = findDuplicateOnceError(form, otherEntries);
    if (dupMsg) errs.valid_on = dupMsg;

    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast("Please fill in valid information", { type: "error" });
      return;
    }

    const sortedRanges = [...form.time_ranges].sort((a, b) => (a.start > b.start ? 1 : -1));

    const payload =
      form.type === "once"
        ? { type: "once", valid_on: form.valid_on, time_ranges: sortedRanges }
        : {
          type: "weekly",
          validity_start: form.validity_start,
          validity_end: form.no_end ? null : form.validity_end,
          no_end: form.no_end,
          repeat_on: selectedWeekdays,
          time_ranges: sortedRanges,
        };

    const result =
      mode === "edit"
        ? await updateQuietHours(storeId, form.id, payload)
        : await saveQuietHours(storeId, payload);

    if (result.success) onSaved();
  };

  const handleCancel = () => {
    if (isDirty()) {
      setShowCancelConfirmation(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => e.preventDefault()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {mode === "edit" ? "Edit Quiet Hours" : "Add Quiet Hours"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ color: "#62626E", mb: 0.1 }}>
              During Quiet Hours, advertising is displayed without sound.
            </Typography>
            <Typography variant="body2" sx={{ color: "#62626E" }}>
              The following Quiet Hours are ignored outside Regular Hours and during Special Hours.
            </Typography>
          </Box>

          <Box sx={{ mb: 2, maxWidth: 260 }}>
            <HtmlTooltipDisabled title={isActiveEdit ? "Not available while the schedule is active" : ""}>
              <span>
                <SingleSelect
                  label="Type"
                  elemId="quiet_hours_type"
                  enableFullWidth={true}
                  options={[
                    { label: "Once", id: "once" },
                    { label: "Weekly", id: "weekly" },
                  ]}
                  defaultVal={
                    form.type === "once"
                      ? { label: "Once", id: "once" }
                      : { label: "Weekly", id: "weekly" }
                  }
                  selectionCheck={(option, value) => option.id === value.id}
                  onChangeCall={(selectedOption) => patch("type", selectedOption.id)}
                  sortOptions={false}
                  disableSearch={true}
                  readOnlyFlag={isActiveEdit}
                />
              </span>
            </HtmlTooltipDisabled>
          </Box>

          {form.type === "weekly" && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Validity
              </Typography>
              {/* Top-aligned, not bottom-aligned: reserveErrorSpace only keeps one
                  error line free, so a two-line error made a bottom-aligned row push
                  that field's label and input upward and break the alignment with its
                  neighbour. Aligned at the top, every date field puts its label and
                  input at the same height and the error grows downward instead. */}
              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                <Box sx={{ width: 200 }}>
                  <HtmlTooltipDisabled title={isActiveEdit ? "Not available while the schedule is active" : ""}>
                    <span>
                      <DateInputField
                        label="Start"
                        elemId="quiet_validity_start"
                        fieldName="validity_start"
                        reserveErrorSpace
                        defaultVal={form.validity_start || ""}
                        fieldError={errors.validity_start || ""}
                        disabled={isActiveEdit}
                        preserveStoredValue={isActiveEdit}
                        minSelectable={isActiveEdit ? undefined : tomorrow}
                        maxSelectable={form.validity_end || undefined}
                        highlightedDates={quietLegend ? highlightQuietDate : undefined}
                        highlightLegend={quietLegend}
                        onChangeCall={(date) =>
                          patch("validity_start", date ? moment(date).format("YYYY-MM-DD") : null)
                        }
                      />
                    </span>
                  </HtmlTooltipDisabled>
                </Box>
                <Box sx={{ width: 200 }}>
                  <HtmlTooltipDisabled title={form.no_end ? "This schedule has no end date" : ""}>
                    <span>
                      <DateInputField
                        label="End"
                        elemId="quiet_validity_end"
                        fieldName="validity_end"
                        reserveErrorSpace
                        defaultVal={form.validity_end || ""}
                        fieldError={form.no_end ? "" : (errors.validity_end || "")}
                        disabled={form.no_end}
                        minSelectable={endMinDate}
                        highlightedDates={quietLegend ? highlightQuietDate : undefined}
                        highlightLegend={quietLegend}
                        onChangeCall={(date) =>
                          patch("validity_end", date ? moment(date).format("YYYY-MM-DD") : null)
                        }
                      />
                    </span>
                  </HtmlTooltipDisabled>
                </Box>
                {/* The checkbox has no label/input of its own, so the top-aligned row
                    would put it at the very top. Padding, not margin — the parent
                    Stack's spacing resets child margins. 24px reproduces where the
                    bottom-aligned row used to place it: the date field's box is 1px
                    (picker offset) + 16px (label gap) + 31px (input) + 16px (reserved
                    error line) tall, and this 28px control sat 12px above its bottom. */}
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt: "24px" }}>
                  <StyledCheckbox
                    checked={form.no_end}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({
                        ...prev,
                        no_end: checked,
                        validity_end: checked ? null : prev.validity_end,
                      }));
                      if (checked) {
                        setErrors((prev) => {
                          if (!prev.validity_end) return prev;
                          const { validity_end, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ position: "relative", top: "1.8px" }}>No End</Typography>
                </Stack>
              </Stack>

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Repeat on
              </Typography>
              {/* flex-start + fixed spacing — space-between stretched the seven
                  weekdays across the full dialog width. */}
              <Stack
                direction="row"
                justifyContent="flex-start"
                spacing={1.5}
                sx={{ mb: errors.repeat_on ? 0.5 : 0 }}
              >
                {WEEKDAY_DISPLAY_ORDER.map((day) => {
                  const disabled = isActiveEdit || !availableWeekdaySet.has(day);
                  const weekdayTooltip = isActiveEdit
                    ? "Not available while the schedule is active"
                    : disabled
                      ? "This weekday is not part of the selected validity range"
                      : "";
                  return (
                    <Stack key={day} alignItems="center" spacing={0.25}>
                      <Tooltip title={weekdayTooltip}>
                        <span>
                          <StyledCheckbox
                            checked={!!form.repeat_on?.[day]}
                            size="small"
                            disabled={disabled}
                            onChange={(e) =>
                              patch("repeat_on", { ...form.repeat_on, [day]: e.target.checked })
                            }
                          />
                        </span>
                      </Tooltip>
                      <Typography variant="caption" sx={{ color: disabled ? "#BDBDBD" : undefined }}>
                        {WEEKDAY_SHORT[day]}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
              {errors.repeat_on && (
                <FormHelperText error sx={{ ml: 0, mt: 0.5, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.3 }}>
                  {errors.repeat_on}
                </FormHelperText>
              )}
            </Box>
          )}

          {form.type === "once" && (
            <Box sx={{ mb: 2, maxWidth: 200 }}>
              <HtmlTooltipDisabled title={isActiveEdit ? "Not available while the schedule is active" : ""}>
                <span>
                  <DateInputField
                    label="Valid on"
                    elemId="quiet_valid_on"
                    fieldName="valid_on"
                    reserveErrorSpace
                    defaultVal={form.valid_on || ""}
                    fieldError={errors.valid_on || ""}
                    disabled={isActiveEdit}
                    preserveStoredValue={isActiveEdit}
                    minSelectable={isActiveEdit ? undefined : tomorrow}
                    highlightedDates={quietLegend ? highlightQuietDate : undefined}
                    highlightLegend={quietLegend}
                    onChangeCall={(date) =>
                      patch("valid_on", date ? moment(date).format("YYYY-MM-DD") : null)
                    }
                  />
                </span>
              </HtmlTooltipDisabled>
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
            Quiet Hours
          </Typography>
          <QuietTimeRangeBlock
            ranges={form.time_ranges}
            showEmptyErrors={errors.time_ranges === "All ranges require start and end times"}
            disabled={isActiveEdit}
            onChange={(ranges) => patch("time_ranges", ranges)}
          />
        </DialogContent>

        <DialogActions>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <CloseButton onClick={handleCancel} label="Cancel" icon={<Close />} />
            <ActionButton onClick={handleSave} label="Save" icon={<Save />} sx={{ mt: 0, mb: 0 }} />
          </Stack>
        </DialogActions>
      </Dialog>

      {showCancelConfirmation && (
        <Confirmation
          showConfirmationPopup={showCancelConfirmation}
          closePopup={() => setShowCancelConfirmation(false)}
          confirmationActionCall={() => {
            setShowCancelConfirmation(false);
            onClose();
          }}
          confirmationMessage="Are you certain you want to discard all the changes you have made?"
          confirmationTitle="Confirmation"
        />
      )}
    </>
  );
};

export default AddEditQuietHoursDialog;
